import React from 'react';
import { Linking, Platform, Pressable, ScrollView, Text, View } from 'react-native';

function P({ children }: { children: React.ReactNode }) {
  return <Text className="mb-3 text-base leading-6 text-zinc-800 dark:text-zinc-200">{children}</Text>;
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <Text className="mb-3 mt-6 text-lg font-bold text-zinc-900 dark:text-zinc-100 first:mt-0">{children}</Text>
  );
}

type PrivacyPolicyWebPageProps = {
  /** Uygulama içi yığında geri üst bilgi kullanılır; web tek başına sayfada ana sayfa linki gösterilir. */
  showHomeLink?: boolean;
};

export function PrivacyPolicyWebPage({ showHomeLink }: PrivacyPolicyWebPageProps = {}) {
  const showLink = showHomeLink !== false && Platform.OS === 'web';

  return (
    <ScrollView className="flex-1 bg-white dark:bg-black">
      <View className="mx-auto w-full max-w-3xl px-5 py-10">
        {showLink ? (
          <Pressable
            accessibilityRole="link"
            onPress={() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/';
              }
            }}
            className="mb-6 self-start active:opacity-70">
            <Text className="text-base font-semibold text-rose-700">← Ana sayfa</Text>
          </Pressable>
        ) : null}

        <Text className="mb-2 text-3xl font-bold text-zinc-950 dark:text-zinc-50">Privacy Policy</Text>
        <Text className="mb-8 text-sm text-zinc-500 dark:text-zinc-400">Last updated: April 28, 2026</Text>

        <P>
          This Privacy Policy is designed to provide clarity about the personal information and data
          collected by Annedaş and/or its agents, consultants, employees, officers, and directors. It
          explains how this information is collected, why it is processed, how and where it is used,
          how long it is retained, and whether it is shared with other parties. By using the Annedaş
          web and mobile applications, you give your explicit and informed consent to the processing of
          your personal information in accordance with this Privacy Policy and Terms of Service.
        </P>

        <P>
          As per definition, &quot;personal data&quot; is any information relating to an individual,
          whether it relates to his or her private, professional, or public life. It can be anything
          from a name, a home address, a photo, an email address, posts on social networking websites,
          or a computer&apos;s IP address.
        </P>

        <H2>Information We Collect</H2>
        <P>
          Annedaş collects data from you through your interactions with our application. Because Annedaş
          is a community platform and marketplace for mothers, much of the data collected is content you
          choose to share.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Registration Information:</Text> Email address,
          password (stored in encrypted form), first name, last name, and profile picture.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">User-Generated Content (UGC):</Text> Forum posts,
          comments, marketplace listings, product descriptions, and photos/images you upload from your
          device gallery.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Usage Data:</Text> IP address, device information,
          browser type, operating system, screen resolution, language preference.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Interaction Data:</Text> Likes, reports, and
          communication metadata within the marketplace.
        </P>

        <H2>Non-Personal Information</H2>
        <P>
          Non-personal information includes technical information, behavioral data, and statistical data.
          This type of data cannot be used to personally identify you and includes device types,
          general location (at the city/country level), and OS versions.
        </P>

        <H2>How We Collect Information</H2>
        <P>
          We work with third-party services, primarily Google&apos;s Firebase infrastructure. Below is a
          concise list of such services and the type of personal information they collect:
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Authentication:</Text> Firebase Authentication
          (Email address, name, unique user identifier).
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Data &amp; Media Storage:</Text> Cloud Firestore
          and Firebase Cloud Storage (User profiles, forum posts, comments, marketplace listings,
          uploaded photos, and user reports).
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Analytics:</Text> Firebase Analytics (Cookies,
          unique device identifiers, and usage data).
        </P>

        <H2>Why We Collect Information</H2>
        <P>
          <Text className="font-semibold text-zinc-900">Service Provision:</Text> To enable you to post in
          the forum, comment, create marketplace listings, and interact with other mothers.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Community Safety &amp; Moderation:</Text> To
          operate our in-app reporting system. When users report inappropriate content or listings, we
          collect this data to review and maintain a safe environment.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">User Experience:</Text> To fix bugs, improve
          application flow, and shape the application according to user preferences.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Security:</Text> To securely authenticate users and
          enforce strict database rules.
        </P>

        <H2>Child Safety Standards &amp; CSAE Policy (Zero Tolerance)</H2>
        <P>
          Annedaş is strictly a community for adult mothers. We have a zero-tolerance policy towards any
          content that exploits or abuses children, including Child Sexual Abuse Material (CSAM) and
          Child Sexual Abuse and Exploitation (CSAE).
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Reporting:</Text> Users can report any concerning
          content immediately using the in-app &quot;Şikayet Et&quot; (Report) button available on all
          forum posts, comments, and marketplace listings.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Action:</Text> Any accounts found uploading,
          sharing, or promoting CSAM/CSAE will be permanently terminated immediately.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Law Enforcement:</Text> We are fully compliant with
          all regional and international laws regarding child safety. We will proactively report any
          illegal activities, user details, and metadata involving child exploitation to the relevant
          national authorities (e.g., NCMEC, local cybercrime units) without hesitation.
        </P>

        <H2>Where and How Long We Retain Information</H2>
        <P>
          <Text className="font-semibold text-zinc-900">Storage:</Text> All your user data, media, and
          community interactions are stored in Firebase Cloud Firestore and Firebase Storage.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Retention Period:</Text> Your data will be retained
          as long as your account is active. When you delete your account, your personal data and uploaded
          content will be permanently deleted from our active databases within 30 days. Analytical data
          collected by third-party services is subject to their respective privacy policies.
        </P>

        <H2>Do We Share Information We Collect</H2>
        <P>
          Annedaş is not a data mining company. We do not sell your personal information to third parties.
          Because Annedaş is a social platform, the content you voluntarily post (forum threads,
          marketplace listings, profile picture, name) is visible to other registered members of the
          community.
        </P>
        <P>We do not disclose your private information unless required by law. We may disclose information about you:</P>
        <P>If required by law, regulation, or legal process (e.g., court order).</P>
        <P>
          In response to requests by government agencies or law enforcement (especially regarding child
          safety violations).
        </P>
        <P>When we believe disclosure is necessary to protect against physical or financial harm.</P>

        <H2>Security</H2>
        <P>To ensure the security of your data, we utilize:</P>
        <P>
          <Text className="font-semibold text-zinc-900">Firestore Security Rules:</Text> Strict data access
          control (e.g., reports can only be created by authenticated users and cannot be altered or read by
          other public users).
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">Secure Authentication:</Text> Powered by Firebase
          Authentication.
        </P>
        <P>
          <Text className="font-semibold text-zinc-900">HTTPS:</Text> All data transmission is encrypted.
        </P>

        <H2>Your Rights</H2>
        <P>
          You have the right to access, correct, or request the deletion of your personal data. You have
          the right to object to processing and to delete your account and all associated data completely.
          You can contact us to exercise these rights.
        </P>
        <P>
          Our application is designed for adult parents (18+) and is not intended for children. We do not
          knowingly collect personal information from minors.
        </P>

        <H2>Contact Us</H2>
        <P>
          If you have any questions or concerns regarding our Privacy Policy or Child Safety standards,
          please contact us by email:{' '}
          <Text
            accessibilityRole="link"
            className="text-rose-700 underline"
            onPress={() => {
              Linking.openURL('mailto:chnakbs@gmail.com').catch(() => {});
            }}>
            chnakbs@gmail.com
          </Text>
        </P>

        <View className="h-16" />
      </View>
    </ScrollView>
  );
}
